/**
 * RunCard — Zeile der Text-Läufe-Liste. Muster `tools/batch/BatchRunCard.tsx`,
 * ergänzt um `completed_with_errors` (amber) + Credits-Zeile (E3: NIE "reserviert").
 */

import { Clock, CheckCircle2, XCircle, Loader2, AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Badge, Progress } from '@/components/v2/ui'
import { cn, formatNumber } from '@/lib/utils'
import type { ChannelContentRun, RunStatus } from '@/lib/api/channel-content-bulk'

import { useTextRunMutations } from './hooks/useSuggestionMutations'

interface RunCardProps {
  run: ChannelContentRun
  /** P1/B4: Basis-Pfad des Run-Detail-Links (Default = Standalone-Tool). */
  runLinkBase?: string
}

type StatusConfig = { icon: typeof Clock; variant: 'default' | 'warning' | 'success' | 'error'; spin?: boolean }

const STATUS_CONFIG: Record<RunStatus, StatusConfig> = {
  pending: { icon: Clock, variant: 'default' },
  running: { icon: Loader2, variant: 'warning', spin: true },
  completed: { icon: CheckCircle2, variant: 'success' },
  completed_with_errors: { icon: AlertTriangle, variant: 'warning' },
  failed: { icon: XCircle, variant: 'error' },
  cancelled: { icon: XCircle, variant: 'default' },
}

const RETRYABLE = new Set<RunStatus>(['failed', 'completed_with_errors'])

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
}

export function RunCard({ run, runLinkBase }: RunCardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { retry } = useTextRunMutations()
  const config = STATUS_CONFIG[run.status] ?? STATUS_CONFIG.pending
  const Icon = config.icon
  const percentage = run.total_pairs > 0 ? (run.done_pairs / run.total_pairs) * 100 : 0
  const open = () => navigate(`${runLinkBase ?? '/v2/tools/text/bulk/runs'}/${run.id}`)

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() } }}
      className="w-full cursor-pointer rounded-lg border border-neutral-200 bg-white p-4 text-left transition-all hover:border-orange-300 dark:border-neutral-800 dark:bg-neutral-900 dark:hover:border-orange-700"
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm text-neutral-500 dark:text-neutral-400">{formatDate(run.created_at)}</span>
        <div className="flex items-center gap-1.5">
          {run.failed_pairs > 0 && (
            <Badge variant="error" size="sm">
              {t('channelContent.runs.failedPairs', { count: run.failed_pairs })}
            </Badge>
          )}
          <Badge variant={config.variant} size="sm" icon={<Icon className={cn('h-3 w-3', config.spin && 'animate-spin')} />}>
            {t(`channelContent.runs.status.${run.status}`)}
          </Badge>
        </div>
      </div>
      <div className="mt-3 space-y-1.5">
        <p className="text-sm text-neutral-700 dark:text-neutral-300">
          {t('channelContent.runs.progress', { done: run.done_pairs, total: run.total_pairs })}
        </p>
        <Progress value={percentage} size="sm" />
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-neutral-100 pt-3 text-sm dark:border-neutral-800">
        <span className="text-neutral-500 dark:text-neutral-400">
          {t('channelContent.runs.creditsEstimated', { count: formatNumber(run.credits_estimated) })}
        </span>
        {run.status !== 'pending' && (
          <span className="font-medium text-neutral-900 dark:text-neutral-100">
            {t('channelContent.runs.creditsSpent', { count: formatNumber(run.credits_spent) })}
          </span>
        )}
      </div>
      {RETRYABLE.has(run.status) && (
        <button
          type="button"
          disabled={retry.isPending}
          onClick={(e) => {
            e.stopPropagation()
            retry.mutate(run.id)
          }}
          className="mt-3 w-full rounded-md border border-orange-300 py-1.5 text-sm font-medium text-orange-600 transition-colors hover:bg-orange-50 disabled:opacity-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-950/20"
        >
          {t('channelContent.runs.retry')}
        </button>
      )}
    </div>
  )
}
